import type { Ref } from 'react';
import { Box, Typography } from '@mui/material';
import { formatNumber } from '../format';

export interface BudgetMeterProps {
  /** Percent of budget consumed (e.g. 118 = 118%). Rounded for display. */
  pct: number;
  ref?: Ref<HTMLDivElement>;
}

/**
 * "% of budget" meter used in the ranked usage tables (and reports/lines): a
 * thin track with a tone-graded fill (green &lt; 80% · amber ≥ 80% · red ≥ 100%)
 * and an over-budget overshoot marker pinned at the right edge when past 100%.
 * Port of the prototype `BudgetMeter`. The numeric label uses the same tone.
 */
export function BudgetMeter({ pct, ref }: BudgetMeterProps) {
  const overshoot = pct >= 100;
  const fillPct = Math.min(100, Math.max(0, pct));
  const tone: 'success' | 'warning' | 'error' =
    pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'success';

  return (
    <Box ref={ref} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: 6,
          borderRadius: '3px',
          bgcolor: 'borderSubtle',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${fillPct}%`,
            bgcolor: `${tone}.main`,
            borderRadius: '3px',
          }}
        />
        {overshoot && (
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: -1,
              bottom: -1,
              width: '3px',
              bgcolor: 'error.dark',
              borderRadius: '1px',
            }}
          />
        )}
      </Box>
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: `${tone}.main`,
          minWidth: 44,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}
      >
        {formatNumber(Math.round(pct))}%
      </Typography>
    </Box>
  );
}
